import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../utils/api.js';
import useSessionPersistence from './useSessionPersistence.jsx';
import { useWorkflow } from '../contexts/WorkflowContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { createLogger } from '../utils/logger.js';
import {
    classifyError,
    isRetryable,
    getOverloadedService,
    logError,
    getErrorMessage,
    ERROR_TYPES,
    classifyFrontendError,
    showEnhancedErrorUI,
    showPromptsError as showPromptsErrorUtil,
    showToast as showToastUtil
} from '../utils/errorHandler.js';
import { logDataComparison } from '../utils/dataComparison.js';

const log = createLogger('useArchitectApp');

// Custom hook to manage The Architect application state and flow
export function useArchitectApp() {
    // 🔧 FIX: Get auth state to prevent unnecessary API calls for guests
    const { authType, loading: authLoading } = useAuth();

    // Session persistence hook
    const { scheduleAutoSave, canSaveSessions } = useSessionPersistence();

    // Workflow context - centralized state for cross-component communication
    // 🔄 UNIFIED ARCHITECTURE: Single workflow context handles both queue and running states
    // Agent WebSocket auto-syncs all state (queue position, workflow progress)
    const {
        workflowState: contextWorkflowState,
        isWorkflowMode: contextIsWorkflowMode,
        updateWorkflowState: updateContextWorkflowState,
        startWorkflow: startContextWorkflow,
        clearWorkflow: clearContextWorkflow,
        cancelWorkflow: cancelContextWorkflow,
    } = useWorkflow();

    // Session reuse state
    const [activeSession, setActiveSession] = useState(null);
    const [sessionCheckLoading, setSessionCheckLoading] = useState(true);
    const [showResumeDialog, setShowResumeDialog] = useState(false);

    // Main V2 Flow state
    const [v2Data, setV2Data] = useState({
        originalIdea: null,
        platform: null,
        research: null,
        validation: null,
        answers: [],
        analysis: null,
        prompts: null,
        flowDiagram: null,
        userId: null, // Add userId to track user for credit system
        stepTimings: {} // Track actual processing times from Agent responses
    });

    // Production mode - debug utility removed

    // UI state
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTitle, setLoadingTitle] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('');
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    // Production mode - debug state removed

    // Modal state
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [currentPromptModalData, setCurrentPromptModalData] = useState(null);

    // Toast state
    const [toasts, setToasts] = useState([]);

    // 🔄 Queue system state - now managed by WorkflowContext
    // Local state removed - use contextQueueState and contextIsQueueMode instead

    // Status tags state
    const [statusTags, setStatusTags] = useState({
        analyzing: false,
        analysisComplete: false,
        promptsComplete: false,
        platform: ''
    });

    // Flow diagram state
    const [flowDiagramLoaded, setFlowDiagramLoaded] = useState(false);
    const [flowDiagramError, setFlowDiagramError] = useState(null);

    // Error states
    const [analysisError, setAnalysisError] = useState(null);
    const [promptsError, setPromptsError] = useState(null);

    // Results display state
    const [showAnalysisResults, setShowAnalysisResults] = useState(false);
    const [showPromptsTab, setShowPromptsTab] = useState(false);
    
    // Question flow state  
    const [shouldStartQuestions, setShouldStartQuestions] = useState(false);
    
    // Retry state for API overloads
    const [stepRetryInfo, setStepRetryInfo] = useState(null);
    const [showRetryOption, setShowRetryOption] = useState(false);
    
    // Batch prompt generation progress tracking
    const [promptGenerationProgress, setPromptGenerationProgress] = useState({
        currentBatch: 0,
        totalBatches: 5,
        completedPrompts: 0,
        targetPrompts: 25,
        isGenerating: false,
        batchStatus: [], // Track status of each batch
        estimatedTimeRemaining: null,
        startTime: null,
        batchTimes: [] // Track time for each batch to estimate remaining
    });

    // Refs for session management
    const sessionId = useRef(null);
    const isCheckingSessionRef = useRef(false);

    // 🚀 Workflow state (moved to top to fix hoisting issues)
    // Initialize from context if workflow is already active (for navigation back to page)
    const [workflowState, setWorkflowState] = useState(() => {
        if (contextWorkflowState?.isActive) {
            return {
                workflowId: contextWorkflowState.workflowId,
                agentId: contextWorkflowState.agentId,
                status: contextWorkflowState.status,
                currentStep: contextWorkflowState.stepName,
                progress: contextWorkflowState.progress,
                batchProgress: contextWorkflowState.batchProgress,
            };
        }
        return null;
    });
    const [isWorkflowMode, setIsWorkflowMode] = useState(() => contextIsWorkflowMode || false);
    const [resumeWorkflowData, setResumeWorkflowData] = useState(null);

    // Track if we've synced from context to avoid re-syncing
    const hasSyncedFromContextRef = useRef(false);

    // WebSocket handles real-time updates via WorkflowContext

    // Helper functions that need to be defined early
    const getStepNumber = (stepName) => {
        const stepMap = {
            'research': 2,
            'validation': 3,
            'analysis': 4,
            'prompts': 5,
            'diagrams': 6
        };
        return stepMap[stepName] || 1;
    };

    // Update step data from workflow status
    const updateStepData = (data) => {
        setV2Data(prev => {
            const updates = {};
            if (data.research && !prev.research) updates.research = data.research;
            if (data.validation && !prev.validation) updates.validation = data.validation;
            if (data.analysis && !prev.analysis) updates.analysis = data.analysis;
            if (data.prompts && !prev.prompts) updates.prompts = data.prompts;
            if (data.diagrams && !prev.flowDiagram) updates.flowDiagram = data.diagrams;
            
            return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
    };

    // Platform indicator (moved up to fix hoisting issues)
    const showPlatformIndicator = useCallback((platform) => {
        setStatusTags(prev => ({ ...prev, platform }));
    }, []);

    // WebSocket handles real-time updates via WorkflowContext
    // No polling needed - context receives messages and updates state

    // Sync from context when returning to page with active workflow
    // WebSocket updates flow through context - no polling needed
    useEffect(() => {
        // Only sync once per mount, and only if context has active workflow but local doesn't
        if (contextIsWorkflowMode && !isWorkflowMode && !hasSyncedFromContextRef.current) {
            hasSyncedFromContextRef.current = true;

            // Context has active workflow but local state doesn't - sync it
            setIsWorkflowMode(true);
            setIsLoading(true);
            if (contextWorkflowState) {
                setWorkflowState({
                    workflowId: contextWorkflowState.workflowId,
                    agentId: contextWorkflowState.agentId,
                    status: contextWorkflowState.status,
                    currentStep: contextWorkflowState.stepName,
                    progress: contextWorkflowState.progress,
                    batchProgress: contextWorkflowState.batchProgress,
                });
                setCurrentStep(contextWorkflowState.currentStep || 1);
                setLoadingTitle(contextWorkflowState.loadingTitle || 'Processing');
                setLoadingMessage(contextWorkflowState.loadingMessage || '');
                // WebSocket connection is managed by context - no polling needed
            }
        }

        // Reset sync flag when workflow ends
        if (!contextIsWorkflowMode) {
            hasSyncedFromContextRef.current = false;
        }
    }, [contextIsWorkflowMode, contextWorkflowState, isWorkflowMode]);

    // Error handling functions (moved up to fix hoisting issues)
    const showError = useCallback((error, fallbackMessage = 'An error occurred') => {
        // Use enhanced error display for better UX (matching original pattern)
        const message = typeof error === 'string' ? error : fallbackMessage;
        const errorObj = typeof error === 'string' ? new Error(error) : error;
        
        setError(errorObj); // Store the full error object, not just the message
        setIsLoading(false);
        
        // Skip DOM manipulation for insufficient credits - let React handle it
        if (errorObj.type === 'insufficient_credits' || errorObj.status === 402) {
            return;
        }
        
        // Show enhanced error UI if available for other errors
        if (typeof document !== 'undefined') {
            showEnhancedErrorUI(errorObj, message, {
                errorStateId: 'errorState',
                errorMessageId: 'errorMessage',
                errorDetailsId: 'errorDetails',
                retryButtonId: 'retryAnalysisBtn',
                onRetry: () => {
                    clearError();
                    // Retry logic would be handled by specific components
                }
            });
        }
        
    }, []);

    const showAnalysisError = useCallback((error) => {
        
        // Use enhanced error classification for analysis errors
        const classification = classifyFrontendError(error, 'Analysis failed');
        
        setAnalysisError({
            message: classification.userMessage,
            details: classification.details,
            errorType: classification.errorType,
            isRetryable: classification.isRetryable,
            timestamp: new Date().toISOString()
        });
        setIsLoading(false);
        
        // Show enhanced error UI
        if (typeof document !== 'undefined') {
            showEnhancedErrorUI(error, 'Analysis failed', {
                errorStateId: 'errorState',
                errorMessageId: 'errorMessage',
                errorDetailsId: 'errorDetails',
                retryButtonId: 'retryAnalysisBtn',
                onRetry: () => {
                    clearError();
                    setV2Data(prev => ({ ...prev, analysis: null }));
                    stepFunctionsRef.current.runAnalysisStep?.();
                }
            });
        }
    }, []);

    const showPromptsError = useCallback((message) => {
        setPromptsError(message);
        
        // Use enhanced prompts error display
        if (typeof document !== 'undefined') {
            showPromptsErrorUtil(message, {
                loadingStateId: 'promptsLoadingState',
                resultsId: 'promptsResults',
                errorStateId: 'promptsErrorState'
            });
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
        setAnalysisError(null);
        setPromptsError(null);
        setShowRetryOption(false);
        setStepRetryInfo(null);
    }, []);

    // Handle workflow completion
    const handleWorkflowCompletion = useCallback((workflowData, workflowId) => {
        setIsLoading(false);
        setIsWorkflowMode(false);
        setWorkflowState(null); // 🔧 FIX: Clear workflow state to prevent UI showing stale "started" status
        setCurrentStep(6); // Final step

        // 🔧 CRITICAL FIX: Do NOT clear context workflow on completion!
        // The context data (sessionId, workflowId, completed data) is needed by
        // the analyze page to display results. Context already sets status='completed'
        // and isActive=false which is enough to mark workflow as done.
        // clearContextWorkflow(); // REMOVED - was causing "No session found" error

        // Update all step data
        if (workflowData) {
            setV2Data(prev => {
                const newData = {
                    ...prev,
                    // 🔧 FIX: Include idea, platform, and answers from backend broadcast
                    originalIdea: workflowData.idea || prev.originalIdea,
                    platform: workflowData.platform || prev.platform,
                    answers: workflowData.answers || prev.answers || [],
                    research: workflowData.research || prev.research,
                    validation: workflowData.validation || prev.validation,
                    analysis: workflowData.analysis || prev.analysis,
                    prompts: workflowData.prompts || prev.prompts,
                    // 🔧 FIX: Add 'diagram' fallback - Agent stores as 'diagram' but returns as 'diagrams'
                    flowDiagram: workflowData.diagrams || workflowData.diagram || workflowData.flowDiagram || prev.flowDiagram
                };

                // Log comparison HERE after state is computed
                if (workflowId) {
                    setTimeout(() => {
                        logDataComparison(workflowId, workflowData, newData);
                    }, 100); // Small delay to ensure React processes the update
                }

                return newData;
            });
        }

        // Display results
        setShowAnalysisResults(true);
        setShowPromptsTab(true);
        setActiveTab('overview');

        // Show platform indicator
        if (v2Data.platform) {
            showPlatformIndicator(v2Data.platform);
        }

        // Update status tags
        setStatusTags({
            analyzing: false,
            analysisComplete: true,
            promptsComplete: true,
            platform: v2Data.platform
        });

        // Refresh credits after workflow completion to show final balance
        if (typeof window !== 'undefined' && window.refreshCredits) {
            window.refreshCredits();
        }

        // WebSocket cleanup handled by context

    }, [v2Data.platform, showPlatformIndicator]);

    // Handle workflow error
    const handleWorkflowError = useCallback((error) => {
        setIsLoading(false);
        setIsWorkflowMode(false);
        showError(error);

        // Clear WorkflowContext state (disconnects WebSocket)
        clearContextWorkflow();

    }, [showError, clearContextWorkflow]);

    // Watch for workflow completion/error from WebSocket via context
    const prevContextStatusRef = useRef(contextWorkflowState?.status);
    const prevIsWorkflowModeRef = useRef(isWorkflowMode);
    useEffect(() => {
        const prevStatus = prevContextStatusRef.current;
        const currentStatus = contextWorkflowState?.status;
        const wasWorkflowMode = prevIsWorkflowModeRef.current;
        const justEnteredWorkflowMode = isWorkflowMode && !wasWorkflowMode;

        // 🔍 DEBUG: Log status transitions to diagnose navigation issues
        if (prevStatus !== currentStatus || currentStatus === 'completed' || justEnteredWorkflowMode) {
            log.log('Status transition check:', {
                prevStatus,
                currentStatus,
                isWorkflowMode,
                wasWorkflowMode,
                justEnteredWorkflowMode,
                hasData: !!contextWorkflowState?.data,
                willProcess: (prevStatus !== currentStatus || justEnteredWorkflowMode) && isWorkflowMode
            });
        }

        // Update refs
        prevContextStatusRef.current = currentStatus;
        prevIsWorkflowModeRef.current = isWorkflowMode;

        // 🔒 FIX: Handle resume case where workflow is already completed
        // When we just enter workflow mode and status is already 'completed',
        // we need to process it even though status didn't "change"
        const shouldProcess = (prevStatus !== currentStatus) ||
            (justEnteredWorkflowMode && currentStatus === 'completed');

        if (!shouldProcess) return;
        if (!isWorkflowMode) return;

        // Handle completion
        if (currentStatus === 'completed' && contextWorkflowState?.data) {
            log.log('WebSocket workflow completed, updating state');
            handleWorkflowCompletion(contextWorkflowState.data, contextWorkflowState.workflowId);
        }

        // Handle error
        if (currentStatus === 'failed' && contextWorkflowState?.error) {
            log.log('WebSocket workflow failed:', contextWorkflowState.error);
            handleWorkflowError(contextWorkflowState.error);
        }

        // Update loading state based on step progress
        if (currentStatus === 'in_progress' && contextWorkflowState) {
            setCurrentStep(contextWorkflowState.currentStep || 1);
            setLoadingTitle(contextWorkflowState.loadingTitle || 'Processing');
            setLoadingMessage(contextWorkflowState.loadingMessage || '');

            // Update batch progress if available
            if (contextWorkflowState.batchProgress) {
                setPromptGenerationProgress(prev => ({
                    ...prev,
                    currentBatch: contextWorkflowState.batchProgress.currentBatch || prev.currentBatch,
                    totalBatches: contextWorkflowState.batchProgress.totalBatches || prev.totalBatches,
                    completedPrompts: contextWorkflowState.batchProgress.completedPrompts || prev.completedPrompts,
                    targetPrompts: contextWorkflowState.batchProgress.targetPrompts || prev.targetPrompts,
                    isGenerating: true,
                }));
            }
        }
    }, [contextWorkflowState, isWorkflowMode, handleWorkflowCompletion, handleWorkflowError]);

    // Toast management (moved early to avoid hoisting issues)
    const showToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        const toast = { id, message, type };
        setToasts(prev => [...prev, toast]);
        
        // Also show enhanced toast if in browser
        if (typeof document !== 'undefined') {
            showToastUtil(message, type);
        }
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Handle workflow cancellation cleanup (must be defined before cancelWorkflow)
    const handleWorkflowCancellation = useCallback((finalData) => {
        // Reset UI state
        setIsLoading(false);
        setIsWorkflowMode(false);
        setWorkflowState(null);

        // Clear WorkflowContext state (disconnects WebSocket)
        clearContextWorkflow();

        // Preserve any completed step data
        if (finalData) {
            setV2Data(prev => ({
                ...prev,
                ...finalData,
                cancelled: true,
                cancelledAt: new Date().toISOString()
            }));
        }

        // Show success message
        showToast('Workflow cancelled successfully', 'info');

    }, [setIsLoading, setIsWorkflowMode, setWorkflowState, setV2Data, showToast, clearContextWorkflow]);

    // Cancel workflow function - uses WebSocket via context
    const cancelWorkflow = useCallback(async (reason = 'User cancelled') => {
        try {
            if (!workflowState?.workflowId && !contextWorkflowState?.workflowId) {
                throw new Error('No active workflow to cancel');
            }

            // Cancel via WebSocket (context handles the communication)
            const result = await cancelContextWorkflow(reason);

            if (!result.success) {
                throw new Error(result.error || 'Failed to cancel workflow');
            }

            // Local cleanup (context handles WebSocket state)
            handleWorkflowCancellation();

            return result;

        } catch (error) {
            showError(error);
            throw error;
        }
    }, [workflowState, contextWorkflowState, cancelContextWorkflow, showError, handleWorkflowCancellation]);

    // Check for active session - moved up to avoid hoisting issues
    const checkForActiveSession = useCallback(async () => {
        // Prevent concurrent session checks
        if (isCheckingSessionRef.current) {
            return;
        }

        try {
            isCheckingSessionRef.current = true;
            setSessionCheckLoading(true);
            const response = await api.getActiveSession();
            
            if (response.session) {
                const validation = await api.validateSession(response.session.id);
                
                if (validation.canResume) {
                    // For now, just set the active session - we'll implement full resume logic later
                    setActiveSession(response.session);
                } else {
                    // Abandon invalid session
                    await api.abandonSession(response.session.id);
                }
            }
        } catch (error) {
            // Silently handle session check errors
        } finally {
            setSessionCheckLoading(false);
            isCheckingSessionRef.current = false;
        }
    }, []);

    // Check for resumable workflows using backend session state
    const checkForResumableWorkflow = useCallback(async () => {
        try {
            // 🔧 CRITICAL FIX: Skip if WorkflowContext already has active workflow
            // This prevents duplicate API calls and state conflicts when navigating
            // back to Home while a workflow is running
            if (contextIsWorkflowMode) {
                log.log('Skipping resumable check - context already has active workflow');
                return;
            }

            // Query backend for user's active workflow
            const activeSession = await api.request('/api/v2/session/active');

            if (!activeSession.success || !activeSession.workflow) {
                return;
            }
            
            const { workflowId, agentId, status: workflowStatus } = activeSession.workflow;
            
            // 🔧 CRITICAL FIX: Validate workflowId exists before making API call
            if (!workflowId || workflowId === 'undefined') {
                return;
            }

            // Get detailed workflow status
            const status = await api.request(`/api/v2/workflow/status?workflowId=${workflowId}&agentId=${agentId}`);
            
            if (status.success) {
                // Handle completed workflows
                if (status.status === 'completed' && status.data) {
                    updateStepData(status.data);
                    setShowAnalysisResults(true);
                    setActiveTab('overview');
                } else if (status.status === 'cancelled') {
                    if (status.data) {
                        setV2Data(prev => ({
                            ...prev,
                            ...status.data,
                            cancelled: true,
                            cancelledAt: status.cancelledAt || new Date().toISOString()
                        }));
                    }
                    // Show the cancellation toast only once per workflowId per session
                    try {
                        const toastKey = `cancelToast:${workflowId}`;
                        if (!sessionStorage.getItem(toastKey)) {
                            showToast('Workflow was cancelled', 'info');
                            sessionStorage.setItem(toastKey, '1');
                        }
                    } catch {}
                } else if (['failed', 'error'].includes(status.status)) {
                    showToast('Previous workflow encountered an error', 'error');
                } else {
                    // Check if workflow is actually complete based on data (not just status)
                    const isDataComplete = status.data && status.research && status.validation &&
                        status.analysis && status.prompts && (status.flowDiagram || status.diagrams);

                    if (isDataComplete) {
                        // Load completed data into UI without resuming polling
                        updateStepData(status.data);
                        setIsWorkflowMode(false);
                        setCurrentStep(7); // Set to final step
                        setShowAnalysisResults(true);
                        setShowPromptsTab(true);
                        showToast('Previous session loaded successfully', 'success');
                    } else {
                        // Still active and incomplete - connect via WebSocket
                        setIsWorkflowMode(true);
                        setIsLoading(true);

                        // Set workflow state
                        setWorkflowState({
                            workflowId,
                            agentId,
                            status: status.status
                        });

                        // Start workflow in context (connects WebSocket)
                        startContextWorkflow({ workflowId, agentId, sessionId: sessionId.current });

                        // Restore any existing data
                        if (status.data) {
                            updateStepData(status.data);
                        }
                    }
                }
            }

        } catch (error) {
            // Silently handle resumable workflow check errors
        }
    }, [contextIsWorkflowMode]); // Check context state before making API calls

    // Check for active session on component mount
    // Note: Workflow resumption is handled by WorkflowContext, which checks the
    // backend for active workflows and syncs state via the sync useEffect above.
    // This prevents duplicate API calls and state conflicts.
    //
    // 🔧 FIX: Only runs when user is authenticated to prevent 401 errors for guests
    useEffect(() => {
        if (!authLoading && authType === 'authenticated') {
            checkForActiveSession();
        }
        // Note: checkForResumableWorkflow() removed - WorkflowContext handles this
    }, [authLoading, authType]); // 🔧 FIX: Run when auth state is determined

    // Auto-save session state when data changes
    useEffect(() => {
        if (canSaveSessions && sessionId.current && (v2Data.research || v2Data.validation || v2Data.analysis || v2Data.prompts)) {
            const sessionData = {
                sessionId: sessionId.current,
                originalIdea: v2Data.originalIdea,
                platform: v2Data.platform,
                research: v2Data.research,
                validation: v2Data.validation,
                answers: v2Data.answers,
                analysis: v2Data.analysis,
                prompts: v2Data.prompts,
                flowDiagram: v2Data.flowDiagram,
                currentStep,
                userId: v2Data.userId
            };

            scheduleAutoSave(sessionData);
        }
    }, [v2Data, currentStep, canSaveSessions, scheduleAutoSave]);
    
    // Refs for step functions to avoid dependency cycle issues
    const stepFunctionsRef = useRef({});

    // Enhanced error detection using API-processed errors
    const isRetryableError = useCallback((error) => {
        // If error is already processed by API layer, use its properties
        if (error._processedByErrorHandler && error.isRetryable !== undefined) {
            return error.isRetryable;
        }
        // Fallback to centralized error handler
        return isRetryable(error);
    }, []);

    // Get service name using API-processed errors
    const getOverloadedServiceName = useCallback((error) => {
        // If error is already processed by API layer, use its properties
        if (error._processedByErrorHandler && error.service) {
            return error.service;
        }
        // Fallback to centralized error handler
        return getOverloadedService(error);
    }, []);

    // Enhanced logging using API-processed error details
    const logAPIError = useCallback((step, error, platform = 'unknown', context = {}) => {
        // If error is already processed by API layer, use its classification
        if (error._processedByErrorHandler && error.classification) {
            
            return error.classification;
        }
        
        // Fallback to centralized error handler
        const classification = logError(error, step, platform, context);
        
        
        return classification;
    }, []);


    // Session reuse management functions - checkForActiveSession moved to top

    const isRecentSession = useCallback((session, thresholdMinutes = 30) => {
        if (!session.updated_at) return false;
        
        const sessionTime = new Date(session.updated_at);
        const now = new Date();
        const minutesDiff = (now - sessionTime) / (1000 * 60);
        
        return minutesDiff <= thresholdMinutes;
    }, []);

    // Restore UI from a session object (no network calls, no WebSocket)
    // Used by resume dialog to restore local state from a saved session
    const createNewSession = useCallback(async () => {
        try {
            // Reset all state
            sessionId.current = null;
            setV2Data({
                originalIdea: null,
                platform: null,
                research: null,
                validation: null,
                answers: [],
                analysis: null,
                prompts: null,
                flowDiagram: null,
                userId: api.getCurrentUserId(),
                stepTimings: {}
            });
            setCurrentStep(1);
            setShowAnalysisResults(false);
            setShowPromptsTab(false);
            setStatusTags({
                analyzing: false,
                analysisComplete: false,
                promptsComplete: false,
                platform: ''
            });
            setActiveSession(null);
            setShowResumeDialog(false);
            
        } catch (error) {
        }
    }, []);

    const handleStartFresh = useCallback(async () => {
        try {
            if (activeSession) {
                await api.abandonSession(activeSession.id);
            }
            
            // Reset all state inline to avoid dependency issues
            sessionId.current = null;
            setV2Data({
                originalIdea: null,
                platform: null,
                research: null,
                validation: null,
                answers: [],
                analysis: null,
                prompts: null,
                flowDiagram: null,
                userId: api.getCurrentUserId(),
                stepTimings: {}
            });
            setCurrentStep(1);
            setShowAnalysisResults(false);
            setShowPromptsTab(false);
            setStatusTags({
                analyzing: false,
                analysisComplete: false,
                promptsComplete: false,
                platform: ''
            });
            setActiveSession(null);
            setShowResumeDialog(false);
            
        } catch (error) {
        }
    }, [activeSession]);

    // Initialize session
    const initializeSession = useCallback(async (idea, platform, options = {}) => {
        try {
            // Get current user ID for credit tracking
            const userId = api.getCurrentUserId();
            
            
            const sessionResponse = await api.createSession(idea, platform, {
                ...options,
                userId // Add userId to session creation for credit tracking
            });
            sessionId.current = sessionResponse.sessionId;
            sessionStorage.setItem('currentSessionId', sessionId.current);
            api.setCurrentSessionId(sessionId.current);

            setV2Data(prev => ({
                ...prev,
                originalIdea: idea,
                platform: platform,
                answers: options.answers || [],
                enableWebSearch: options.enableWebSearch ?? true,
                userId: userId // Store userId in component state
            }));

            setStatusTags(prev => ({
                ...prev,
                platform: platform
            }));

            return sessionId.current;
        } catch (error) {
            throw error;
        }
    }, []);

    // Set session ID (for analyze page initialization)
    const setSessionId = useCallback((sessionIdValue) => {
        sessionId.current = sessionIdValue;
        sessionStorage.setItem('currentSessionId', sessionIdValue);
        api.setCurrentSessionId(sessionIdValue);
    }, []);

    // Load existing session
    const loadSession = useCallback(async (sessionIdParam) => {
        log.log('Starting load for session:', sessionIdParam);
        try {
            sessionId.current = sessionIdParam;

            // Check WorkflowContext first for active workflow data
            // The Agent broadcasts data via WebSocket before it's synced to DB,
            // so context may have fresher data than the database.
            // For cancelled/failed sessions, context will be empty → falls through to DB
            const hasContextData = contextWorkflowState?.data && (
                contextWorkflowState.data.research ||
                contextWorkflowState.data.validation ||
                contextWorkflowState.data.analysis ||
                contextWorkflowState.data.prompts ||
                contextWorkflowState.data.flowDiagram
            );

            log.log('hasContextData:', hasContextData);
            if (hasContextData) {
                log.log('Using context data from workflow:', contextWorkflowState.status);
                const contextData = contextWorkflowState.data;
                const userId = api.getCurrentUserId();

                // Unwrap prompts if nested
                const rawPrompts = contextData.prompts;
                const unwrappedPrompts = rawPrompts?.prompts || rawPrompts;

                setV2Data(prev => ({
                    ...prev,
                    // 🔧 FIX: Use context data for idea/platform/answers (not prev.*)
                    originalIdea: contextData.idea || prev.originalIdea,
                    platform: contextData.platform || prev.platform,
                    research: contextData.research || prev.research,
                    validation: contextData.validation || prev.validation,
                    answers: contextData.answers || prev.answers || [],
                    analysis: contextData.analysis || prev.analysis,
                    prompts: unwrappedPrompts || prev.prompts,
                    flowDiagram: contextData.flowDiagram || contextData.diagrams || contextData.diagram || prev.flowDiagram,
                    userId: userId
                }));

                setStatusTags(prev => ({
                    ...prev,
                    platform: contextData.platform || prev.platform
                }));

                // Show results if we have analysis or research data
                if (contextData.analysis || contextData.research) {
                    setShowAnalysisResults(true);
                }
                if (contextData.prompts) {
                    setShowPromptsTab(true);
                }

                return { id: sessionIdParam, ...contextData };
            }

            // Fallback: Load from database API
            log.log('Loading from DB API for session:', sessionIdParam);
            const sessionData = await api.getSession(sessionIdParam);
            log.log('DB API response:', sessionData);

            // Get current user ID for credit tracking
            const userId = api.getCurrentUserId();

            // Unwrap prompts if they're nested (same pattern as analysis unwrapping)
            const rawPrompts = sessionData.session.prompts;
            const unwrappedPrompts = rawPrompts?.prompts || rawPrompts;

            setV2Data(prev => ({
                ...prev,
                originalIdea: sessionData.session.idea,
                platform: sessionData.session.platform,
                research: sessionData.session.research || null,
                validation: sessionData.session.validation || null,
                answers: sessionData.session.answers || [],
                analysis: sessionData.session.analysis || null,
                prompts: unwrappedPrompts || null,
                flowDiagram: sessionData.session.flowDiagram || sessionData.session.flow_diagram || null,
                userId: userId // Add userId when loading session
            }));


            setStatusTags(prev => ({
                ...prev,
                platform: sessionData.session.platform
            }));

            // Show results if we have analysis or research data
            if (sessionData.session.analysis || sessionData.session.research) {
                setShowAnalysisResults(true);
            }
            if (sessionData.session.prompts || unwrappedPrompts) {
                setShowPromptsTab(true);
            }

            log.log('Successfully loaded session data');
            return sessionData.session;
        } catch (error) {
            log.error('loadSession ERROR:', error);
            throw error;
        }
    }, [contextWorkflowState]);

    // Resume existing session - uses same queue mechanism as fresh start
    // Calls workflow/start.js with resume: true
    const resumeSession = useCallback(async (sessionIdParam) => {
        log.log('Starting resume for session:', sessionIdParam);
        try {
            sessionId.current = sessionIdParam;

            const userId = api.getCurrentUserId();
            if (!userId) {
                throw new Error('Not authenticated - cannot resume session');
            }

            // First check session status to avoid 400 errors for completed sessions
            const sessionData = await api.getSession(sessionIdParam);
            if (sessionData?.session?.status === 'completed') {
                log.log('Session already completed, loading data...');
                return loadSession(sessionIdParam);
            }

            // Session is not completed - proceed with resume flow
            const host = import.meta.env.DEV ? 'http://localhost:8787' : '';
            const resumeResponse = await fetch(`${host}/api/v2/workflow/start`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume: true,
                    sessionId: sessionIdParam
                })
            });

            if (!resumeResponse.ok) {
                const errorData = await resumeResponse.json().catch(() => ({}));

                // Fallback: session may have completed between check and call
                if (errorData.error === 'session_already_completed') {
                    log.log('Session completed (fallback), loading data...');
                    return loadSession(sessionIdParam);
                }

                throw new Error(errorData.error || `Resume failed: ${resumeResponse.status}`);
            }

            const resumeData = await resumeResponse.json();
            log.log('Resume response:', resumeData);

            // Populate v2Data with context (idea, platform, completed steps)
            if (resumeData.idea || resumeData.data) {
                const stepData = resumeData.data || {};
                setV2Data(prev => ({
                    ...prev,
                    originalIdea: resumeData.idea || prev.originalIdea,
                    platform: resumeData.platform || prev.platform,
                    answers: resumeData.answers || prev.answers || [],
                    research: stepData.research || prev.research,
                    validation: stepData.validation || prev.validation,
                    analysis: stepData.analysis || prev.analysis,
                    prompts: stepData.prompts || prev.prompts,
                    flowDiagram: stepData.flowDiagram || prev.flowDiagram,
                    userId: userId
                }));

                if (resumeData.platform) {
                    setStatusTags(prev => ({ ...prev, platform: resumeData.platform }));
                }
            }

            // Set loading state
            setIsLoading(true);
            setCurrentStep(2); // Will be updated by WebSocket

            // Connect WebSocket via WorkflowContext (same as fresh run)
            startContextWorkflow({
                workflowId: resumeData.workflowId,
                agentId: resumeData.agentId,
                sessionId: sessionIdParam,
                status: resumeData.status, // 'queued' or 'resuming'
                ...(resumeData.position && { position: resumeData.position }),
                ...(resumeData.estimatedWaitMinutes && { estimatedWaitMinutes: resumeData.estimatedWaitMinutes })
            });

            log.log('WebSocket initiated, status:', resumeData.status);
            return resumeData;

        } catch (error) {
            log.error('resumeSession ERROR:', error);
            // Fallback to loadSession for completed sessions
            log.log('Falling back to loadSession...');
            return loadSession(sessionIdParam);
        }
    }, [loadSession, startContextWorkflow]);

    // Save step data to session
    const saveToSession = useCallback(async (step, data) => {
        try {
            if (sessionId.current) {
                await api.updateSession(sessionId.current, step, data);
            }
        } catch (error) {
        }
    }, []);

    // Loading state management
    const showV2Loading = useCallback((title, message) => {
        setIsLoading(true);
        setLoadingTitle(title);
        setLoadingMessage(message);
        setError(null);
        setAnalysisError(null);
        setPromptsError(null);
    }, []);

    const hideLoading = useCallback(() => {
        setIsLoading(false);
        setLoadingTitle('');
        setLoadingMessage('');
    }, []);

    // Production mode - debug mode toggle removed

    // Production mode - debug step handling removed

    // Modal management
    const openPromptModal = useCallback((promptData) => {
        setCurrentPromptModalData(promptData);
        setShowPromptModal(true);
    }, []);

    const closePromptModal = useCallback(() => {
        setShowPromptModal(false);
        setCurrentPromptModalData(null);
    }, []);

    const copyPromptToClipboard = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast('Prompt copied to clipboard!', 'success');
        } catch (error) {
            showToast('Failed to copy to clipboard', 'error');
        }
    }, [showToast]);

    // ===== MISSING CRITICAL FUNCTIONS FROM ORIGINAL APP.JS =====

    // Display analysis results (MISSING FUNCTION #1)
    const displayAnalysisResults = useCallback((data, promptsData = null) => {
        
        // Hide loading and error states
        hideLoading();
        setError(null);
        
        // Update status tags
        setStatusTags(prev => ({
            ...prev,
            analyzing: false,
            analysisComplete: true
        }));

        // Show results
        setShowAnalysisResults(true);
        
        // Initialize flow diagram component (load on demand when tab is accessed)
        setFlowDiagramLoaded(false); // Reset for fresh load
        
        // Show and initialize prompts if available
        if (promptsData) {
            setShowPromptsTab(true);
            displayPrompts(promptsData);
        }
        
        // Switch to overview tab to show results
        setActiveTab('overview');
        
    }, [hideLoading]);

    // Display prompts (MISSING FUNCTION #2)
    const displayPrompts = useCallback((promptsData) => {
        
        try {
            // Show the prompts tab button
            setShowPromptsTab(true);
            
            // Update completion status
            setStatusTags(prev => ({ ...prev, promptsComplete: true }));
            
            // Auto-switch to prompts tab to show results
            setActiveTab('prompts');
            
            
        } catch (error) {
            showPromptsError(error.message);
        }
    }, []);

    // Show prompt modal with detailed content (MISSING FUNCTION #3)
    const showPromptModalWithContent = useCallback((promptData) => {
        if (!promptData) return;
        
        const confidence = Math.round((promptData.confidence || 0.85) * 100);
        
        // Enhanced modal content building
        let modalContent = `${promptData.prompt || 'No prompt content available'}`;
        
        if (promptData.explanation) {
            modalContent += `\n\n## Why This Matters\n${promptData.explanation}`;
        }
        
        if (promptData.aftercare && promptData.aftercare.length > 0) {
            modalContent += `\n\n## After Completion - What to Do Next\n`;
            promptData.aftercare.forEach((step, index) => {
                modalContent += `${index + 1}. ${step}\n`;
            });
        }
        
        if (promptData.validationCriteria && promptData.validationCriteria.length > 0) {
            modalContent += `\n\n## Success Validation\nCheck these to ensure proper implementation:\n`;
            promptData.validationCriteria.forEach((criteria, index) => {
                modalContent += `☐ ${criteria}\n`;
            });
        }
        
        // Set the enhanced modal data
        setCurrentPromptModalData({
            ...promptData,
            title: promptData.title || 'Development Prompt',
            subtitle: promptData.description || 'Implementation guidance',
            content: modalContent,
            confidence: confidence
        });
        
        setShowPromptModal(true);
        
    }, []);

    // Handle need more info button (MISSING FUNCTION #4)
    const handleNeedMoreInfo = useCallback(async () => {
        // Go back to questions step if validation had questions
        if (v2Data.validation?.questions?.length > 0) {
            try {
                // Questions step no longer needed - handled in Home.jsx
                await stepFunctionsRef.current.runAnalysisStep();
                displayAnalysisResults(v2Data.analysis);
            } catch (error) {
                showToast('Failed to get additional information', 'error');
            }
        } else {
            showToast('No additional questions available', 'info');
        }
    }, [v2Data.validation, v2Data.analysis, displayAnalysisResults, showToast]);

    // Load prompt results from session (MISSING FUNCTION #5)
    const loadPromptResults = useCallback(async () => {
        try {
            if (!sessionId.current) {
                showError('No session found. Please start from the home page.');
                return;
            }
            
            const sessionData = await api.getSession(sessionId.current);
            
            if (sessionData.session.prompts) {
                // Display existing prompts
                displayPrompts(sessionData.session.prompts);
            } else {
                // Generate prompts if they don't exist
                await stepFunctionsRef.current.runPromptsStep();
            }
        } catch (error) {
            showError('Failed to load prompts');
        }
    }, [displayPrompts, showError]);



    // Setup retry for a failed step
    const setupStepRetry = useCallback((stepName, stepData) => {
        setStepRetryInfo({
            step: stepName,
            ...stepData
        });
        setShowRetryOption(true);
        setIsLoading(false);
    }, []);

    // Retry from a specific step
    const retryFromStep = useCallback(async () => {
        if (!stepRetryInfo) {
            return;
        }

        setShowRetryOption(false);
        setStepRetryInfo(null);
        clearError();

        try {
            switch (stepRetryInfo.step) {
                case 'research':
                    await stepFunctionsRef.current.runResearchStep(stepRetryInfo.platform, stepRetryInfo.originalIdea);
                    break;
                case 'validation':
                    await stepFunctionsRef.current.runValidationStep(stepRetryInfo.platform, stepRetryInfo.originalIdea, stepRetryInfo.researchData);
                    break;
                case 'analysis':
                    await stepFunctionsRef.current.runAnalysisStep(stepRetryInfo.platform, stepRetryInfo.originalIdea, stepRetryInfo.researchData, stepRetryInfo.validationData);
                    break;
                case 'prompts':
                    await stepFunctionsRef.current.runPromptsStep(stepRetryInfo.platform);
                    break;
                case 'diagram':
                    await stepFunctionsRef.current.runDiagramStep(stepRetryInfo.platform);
                    break;
                default:
                    throw new Error(`Unknown step to retry: ${stepRetryInfo.step}`);
            }

            // If retry succeeds, continue the flow from next steps
            await continueFlowFromStep(stepRetryInfo.step, stepRetryInfo);
            
        } catch (error) {
            const classification = logAPIError(stepRetryInfo.step, error, stepRetryInfo.platform);
            
            // Use API-processed error properties if available, fallback to classification
            const isRetryable = error._processedByErrorHandler ? error.isRetryable : classification.isRetryable;
            const service = error._processedByErrorHandler ? error.service : classification.service;
            const errorType = error._processedByErrorHandler ? error.errorType : classification.type;
            const userMessage = error._processedByErrorHandler ? error.userMessage : getErrorMessage(errorType, service, error.message).user;
            
            if (isRetryable) {
                setupStepRetry(stepRetryInfo.step, stepRetryInfo);
                showToast(`${service} is still having issues. ${userMessage}`, 'warning');
            } else {
                showError(`Retry failed: ${userMessage}`);
            }
        }
    }, [stepRetryInfo, clearError, isRetryableError, showToast, showError]);

    // Continue flow from a specific step after successful retry
    const continueFlowFromStep = useCallback(async (completedStep, stepData) => {
        try {
            // Get fresh session data for next steps
            const currentSessionData = await api.getSession(sessionId.current);
            const session = currentSessionData.session;

            switch (completedStep) {
                case 'research':
                    // Continue to validation
                    await stepFunctionsRef.current.runValidationStep(stepData.platform, stepData.originalIdea, session.research);
                    
                    // Then to analysis
                    const updatedSessionData = await api.getSession(sessionId.current);
                    await stepFunctionsRef.current.runAnalysisStep(stepData.platform, stepData.originalIdea, session.research, updatedSessionData.session.validation);
                    
                    // Then to prompts
                    await stepFunctionsRef.current.runPromptsStep(stepData.platform);
                    
                    // Then to diagrams
                    await stepFunctionsRef.current.runDiagramStep(stepData.platform);
                    break;
                    
                case 'validation':
                    // Continue to analysis
                    await stepFunctionsRef.current.runAnalysisStep(stepData.platform, stepData.originalIdea, stepData.researchData, session.validation);
                    
                    // Then to prompts
                    await stepFunctionsRef.current.runPromptsStep(stepData.platform);
                    
                    // Then to diagrams
                    await stepFunctionsRef.current.runDiagramStep(stepData.platform);
                    break;
                    
                case 'analysis':
                    // Continue to prompts
                    await stepFunctionsRef.current.runPromptsStep(stepData.platform);
                    
                    // Then to diagrams
                    await stepFunctionsRef.current.runDiagramStep(stepData.platform);
                    break;
                    
                case 'prompts':
                    // Continue to diagrams
                    await stepFunctionsRef.current.runDiagramStep(stepData.platform);
                    break;
                    
                case 'diagram':
                    // Flow complete
                    break;
            }

            // Show final results
            setV2Data(currentV2Data => {
                displayAnalysisResults(currentV2Data.analysis, currentV2Data.prompts);
                return currentV2Data;
            });

        } catch (error) {
            const classification = logAPIError('flow_continuation', error, stepData.platform, {
                completedStep,
                nextStep: getNextStep(completedStep)
            });
            
            if (classification.isRetryable) {
                const errorMsg = getErrorMessage(classification.type, classification.service, error.message);
                const nextStep = getNextStep(completedStep);
                if (nextStep) {
                    setupStepRetry(nextStep, stepData);
                    showToast(`${classification.service} having issues in ${nextStep} step. ${errorMsg.user}`, 'warning');
                }
            } else {
                const errorMsg = getErrorMessage(classification.type, classification.service, error.message);
                showError(`Flow continuation failed: ${errorMsg.user}`);
            }
        }
    }, [displayAnalysisResults, isRetryableError, setupStepRetry, showToast, showError]);

    // Helper to get next step
    const getNextStep = useCallback((currentStep) => {
        const stepOrder = ['research', 'validation', 'analysis', 'prompts'];
        const currentIndex = stepOrder.indexOf(currentStep);
        return currentIndex >= 0 && currentIndex < stepOrder.length - 1 ? stepOrder[currentIndex + 1] : null;
    }, []);

    const retryAnalysis = useCallback(() => {
        setAnalysisError(null);
        setV2Data(prev => ({ ...prev, analysis: null }));
        runAnalysisStep();
    }, []);

    const retryPrompts = useCallback(() => {
        setPromptsError(null);
        setV2Data(prev => ({ ...prev, prompts: null }));
        runPromptsStep();
    }, []);

    // Flow diagram management
    const loadFlowDiagram = useCallback(async (analysisData) => {
        if (flowDiagramLoaded) {
            return;
        }
        
        try {
            setFlowDiagramError(null);
            
            // Simulate flow diagram loading
            // In real implementation, this would call the AppFlowDiagramDisplay component
            setTimeout(() => {
                setFlowDiagramLoaded(true);
            }, 1000);
            
        } catch (error) {
            setFlowDiagramError(`Failed to load flow diagram: ${error.message}`);
        }
    }, [flowDiagramLoaded]);

    // Tab management
    const switchToTab = useCallback((tabName) => {
        setActiveTab(tabName);
        
        // Special handling for flow diagram tab (lazy loading)
        if (tabName === 'flowdiagram' && v2Data.analysis) {
            loadFlowDiagram(v2Data.analysis);
        }
    }, [v2Data.analysis, loadFlowDiagram]);


    // Step 1: Questions (MATCHES ORIGINAL app.js LOGIC)
    const runQuestionsStep = useCallback(async () => {
        setCurrentStep(1);
        
        // Show QuestionFlow component and wait for user interaction (SAME AS ORIGINAL)
        return new Promise((resolve, reject) => {
            try {
                // Set UI state to show questions
                setActiveTab('questions');
                setIsLoading(false); // Stop loading to show questions
                
                            // Set up question completion handlers (SAME AS ORIGINAL)
                        // Clear any existing handlers first to avoid conflicts
            if (window.questionFlowResolve) {
                delete window.questionFlowResolve;
                delete window.questionFlowSkip;
            }
            
            window.questionFlowResolve = async (answers) => {
                try {
                    setV2Data(prev => ({ ...prev, answers: answers }));
                    await saveToSession('answers', answers);
                    
                    // Hide questions tab and continue flow
                    setActiveTab('overview');
                    setIsLoading(true); // Resume loading for next steps
                    setShouldStartQuestions(false); // Reset questions state
                    
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };
            
            window.questionFlowSkip = async (answers) => {
                try {
                    const finalAnswers = answers || [];
                    setV2Data(prev => ({ ...prev, answers: finalAnswers }));
                    await saveToSession('answers', finalAnswers);
                    
                    // Hide questions tab and continue flow
                    setActiveTab('overview');
                    setIsLoading(true); // Resume loading for next steps
                    setShouldStartQuestions(false); // Reset questions state
                    
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };
            
            // Trigger QuestionFlow to start with choices (SAME AS ORIGINAL)
            setShouldStartQuestions(true); // Signal to component to start
                
            } catch (error) {
                reject(error);
            }
        });
    }, [saveToSession]);

    // Step 2: Research (Claude)
    const runResearchStep = useCallback(async (platform = null, originalIdea = null, answers = null) => {
        setCurrentStep(2);

        // Production mode - debug bypass removed

        // Use passed parameters or fallback to v2Data values
        const targetPlatform = platform || v2Data.platform;
        const targetIdea = originalIdea || v2Data.originalIdea;
        
        // Prepare research step with validated answers
        
        const targetAnswers = answers || v2Data.answers;
        
        // Execute research step
        
        showV2Loading('🔬 Research', `Claude is analyzing your idea for ${targetPlatform} platform and creating architecture recommendations...`);
        const stepStartMs = Date.now();
        
        try {
            // Get current user ID for credit tracking
            const userId = v2Data.userId || api.getCurrentUserId();
            
            const data = await api.runResearchWithUserId(
                targetIdea,
                targetAnswers,
                targetPlatform,
                v2Data.enableWebSearch,
                userId
            );
            
            if (!data || !data.research) {
                throw new Error('Invalid research response received');
            }
            
            // Extract timing data from Agent response
            const stepTiming = {
                processingTime: data.research.processingTime || (Date.now() - stepStartMs),
                timestamp: data.research.timestamp || new Date().toISOString()
            };
            
            setV2Data(prev => ({ 
                ...prev, 
                research: data.research,
                stepTimings: {
                    ...prev.stepTimings,
                    research: stepTiming
                }
            }));
            
            await saveToSession('research', data.research);
            
        } catch (error) {
            
            // Special handling for insufficient credits
            if (error.type === 'insufficient_credits' || error.status === 402) {
                // Store the credit error for display
                setError(error);
                hideLoading();
                // Stop the flow completely
                throw error;
            }
            
            const classification = logAPIError('research', error, targetPlatform, {
                originalIdea: targetIdea?.substring(0, 100) + '...',
                answersCount: v2Data.answers?.length || 0
            });
            
            // Check if it's a retryable error - prefer backend-processed error properties
            const isRetryable = error._processedByErrorHandler ? error.isRetryable : classification.isRetryable;
            const serviceName = error._processedByErrorHandler ? error.service : classification.service;
            const errorType = error._processedByErrorHandler ? error.errorType : classification.type;
            
            if (isRetryable) {
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                setupStepRetry('research', {
                    platform: targetPlatform,
                    originalIdea: targetIdea
                });
                showError(`${serviceName} API is having issues (${errorType}). Your progress has been saved - ${errorMsg.user}`);
                // Throw special error to stop the flow
                const retryError = new Error('API_RETRY_NEEDED');
                retryError.isRetryError = true;
                throw retryError;
            } else {
                // For other errors, use centralized error message
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                throw new Error(errorMsg.user);
            }
        } finally {
            hideLoading();
        }
    }, [v2Data.originalIdea, v2Data.answers, v2Data.platform, showV2Loading, hideLoading, saveToSession]);

    // Step 3: Validation (OpenAI)
    const runValidationStep = useCallback(async (platform = null, originalIdea = null, researchData = null, answers = null) => {
        setCurrentStep(3);

        // Production mode - debug bypass removed

        // Use passed parameters or fallback to v2Data values
        const targetPlatform = platform || v2Data.platform;
        const targetIdea = originalIdea || v2Data.originalIdea;
        const targetResearch = researchData || v2Data.research;
        const targetAnswers = answers || v2Data.answers;

        showV2Loading('Validation', `OpenAI is validating and improving the ${targetPlatform} architecture recommendations...`);
        const stepStartMs = Date.now();
        
        try {
            // Get current user ID for credit tracking
            const userId = v2Data.userId || api.getCurrentUserId();
            
            const data = await api.runValidationWithUserId(
                targetResearch,
                targetIdea,
                targetAnswers,
                targetPlatform,
                userId
            );
            
            if (!data || !data.validation) {
                throw new Error('Invalid validation response received');
            }
            
            // Extract timing data from Agent response
            const stepTiming = {
                processingTime: data.validation.processingTime || (Date.now() - stepStartMs),
                timestamp: data.validation.timestamp || new Date().toISOString()
            };
            
            setV2Data(prev => ({ 
                ...prev, 
                validation: data.validation,
                stepTimings: {
                    ...prev.stepTimings,
                    validation: stepTiming
                }
            }));
            
            await saveToSession('validation', data.validation);
            
        } catch (error) {
            const classification = logAPIError('validation', error, targetPlatform, {
                originalIdea: targetIdea?.substring(0, 100) + '...',
                hasResearchData: !!targetResearch,
                answersCount: v2Data.answers?.length || 0
            });
            
            // Check if it's a retryable error - prefer backend-processed error properties
            const isRetryable = error._processedByErrorHandler ? error.isRetryable : classification.isRetryable;
            const serviceName = error._processedByErrorHandler ? error.service : classification.service;
            const errorType = error._processedByErrorHandler ? error.errorType : classification.type;
            
            if (isRetryable) {
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                setupStepRetry('validation', {
                    platform: targetPlatform,
                    originalIdea: targetIdea,
                    researchData: targetResearch
                });
                showError(`${serviceName} API is having issues (${errorType}). Your progress has been saved - ${errorMsg.user}`);
                // Throw special error to stop the flow
                const retryError = new Error('API_RETRY_NEEDED');
                retryError.isRetryError = true;
                throw retryError;
            } else {
                // For other errors, use centralized error message
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                throw new Error(errorMsg.user);
            }
        } finally {
            hideLoading();
        }
    }, [v2Data.research, v2Data.originalIdea, v2Data.answers, v2Data.platform, showV2Loading, hideLoading, saveToSession]);

    // Step 4: Analysis (Claude + OpenAI)
    const runAnalysisStep = useCallback(async (platform = null, originalIdea = null, researchData = null, validationData = null, answers = null) => {
        setCurrentStep(4);

        // Production mode - debug bypass removed

        // Use passed parameters or fallback to v2Data values
        const targetPlatform = platform || v2Data.platform;
        const targetIdea = originalIdea || v2Data.originalIdea;
        const targetResearch = researchData || v2Data.research;
        const targetAnswers = answers || v2Data.answers;

        showV2Loading('Analysis', `AI is performing final analysis and generating comprehensive ${targetPlatform} recommendations...`);
        const stepStartMs = Date.now();
        
        try {
            // Get current user ID for credit tracking
            const userId = v2Data.userId || api.getCurrentUserId();
            
            const data = await api.runAnalysisWithUserId(
                targetResearch,
                targetAnswers,
                targetIdea,
                targetPlatform,
                userId,
                validationData || v2Data.validation // Include validation data from step 2
            );
            
            if (!data || !data.success) {
                throw new Error(data?.error || 'Invalid analysis response received - no real AI analysis was generated');
            }
            
            // Extract timing data from Agent response (analysis has both Claude and OpenAI components)
            const measuredMs = Date.now() - stepStartMs;
            const agentMs = (data.claudeAnalysis?.processingTime || 0) + (data.openaiAnalysis?.processingTime || 0);
            const stepTiming = {
                processingTime: agentMs || measuredMs,
                timestamp: data.claudeAnalysis?.timestamp || data.openaiAnalysis?.timestamp || new Date().toISOString()
            };
            
            setV2Data(prev => ({ 
                ...prev, 
                analysis: data,
                stepTimings: {
                    ...prev.stepTimings,
                    analysis: stepTiming
                }
            }));
            
            await saveToSession('analysis', data);
            
        } catch (error) {
            const classification = logAPIError('analysis', error, targetPlatform, {
                originalIdea: targetIdea?.substring(0, 100) + '...',
                hasResearchData: !!targetResearch,
                hasValidationData: !!validationData,
                answersCount: v2Data.answers?.length || 0
            });
            
            // Check if it's a retryable error - prefer backend-processed error properties
            const isRetryable = error._processedByErrorHandler ? error.isRetryable : classification.isRetryable;
            const serviceName = error._processedByErrorHandler ? error.service : classification.service;
            const errorType = error._processedByErrorHandler ? error.errorType : classification.type;
            
            if (isRetryable) {
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                setupStepRetry('analysis', {
                    platform: targetPlatform,
                    originalIdea: targetIdea,
                    researchData: targetResearch,
                    validationData: validationData
                });
                showAnalysisError(new Error(`${serviceName} API is having issues (${errorType}). Your progress has been saved - ${errorMsg.user}`));
                // Throw special error to stop the flow
                const retryError = new Error('API_RETRY_NEEDED');
                retryError.isRetryError = true;
                throw retryError;
            } else {
                // For other errors, use centralized error message
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                showAnalysisError(new Error(errorMsg.user));
                throw new Error(errorMsg.user);
            }
        } finally {
            hideLoading();
        }
    }, [v2Data.research, v2Data.answers, v2Data.originalIdea, v2Data.platform, showV2Loading, hideLoading, saveToSession, showAnalysisError]);

    // Step 5: Prompts Generation
    const runPromptsStep = useCallback(async (platform = null) => {
        setCurrentStep(5);

        // Production mode - debug bypass removed

        // Use passed platform or fallback to v2Data.platform
        const targetPlatform = platform || v2Data.platform;

        // Initialize batch generation progress
        setPromptGenerationProgress({
            currentBatch: 0,
            totalBatches: null,
            completedPrompts: 0,
            targetPrompts: null,
            isGenerating: true,
            batchStatus: [],
            estimatedTimeRemaining: null,
            startTime: Date.now(),
            batchTimes: []
        });
        
        showV2Loading('Prompts', `Generating ${targetPlatform} prompts...`);
        const stepStartMs = Date.now();
        
        try {
            // Fetch fresh session data to ensure we have the latest analysis results
            // (React state updates are asynchronous, so v2Data might be stale)
            const currentSessionData = await api.getSession(sessionId.current);
            const sessionData = currentSessionData.session;
            
            
            const promptsPayload = {
                originalIdea: sessionData.idea,
                research: sessionData.research,
                validation: sessionData.validation,
                answers: sessionData.answers || [],
                analysis: sessionData.analysis,
                platform: targetPlatform,
                flowDiagram: sessionData.flowDiagram || sessionData.flow_diagram || null
            };
            
            
            // Get current user ID for credit tracking
            const userId = v2Data.userId || api.getCurrentUserId();
            
            // Start batch generation with progress tracking
            const data = await api.generatePromptsWithUserId(promptsPayload, userId, {
                onBatchProgress: (progress) => {
                    // Update progress state when batch completes
                    setPromptGenerationProgress(prev => {
                        const newBatchStatus = [...prev.batchStatus];
                        if (progress.batchIndex !== undefined) {
                            newBatchStatus[progress.batchIndex] = 'completed';
                        }
                        
                        // Calculate estimated time remaining
                        const batchTimes = [...prev.batchTimes, progress.batchTime || 0];
                        const avgBatchTime = batchTimes.reduce((a, b) => a + b, 0) / batchTimes.length;
                        const totalBatches = progress.totalBatches || prev.totalBatches || 1;
                        const remainingBatches = totalBatches - (progress.batchIndex + 1);
                        const estimatedTimeRemaining = Math.round(avgBatchTime * remainingBatches / 1000); // in seconds
                        
                        return {
                            ...prev,
                            currentBatch: progress.batchIndex || prev.currentBatch,
                            completedPrompts: progress.totalCompleted || prev.completedPrompts,
                            totalBatches: totalBatches,
                            targetPrompts: progress.targetPrompts || prev.targetPrompts,
                            batchStatus: newBatchStatus,
                            batchTimes,
                            estimatedTimeRemaining
                        };
                    });
                    
                    // Update loading message with batch progress
                    const totalBatches = progress.totalBatches || 1;
                    const targetPrompts = progress.targetPrompts || 1;
                    const progressPercent = Math.round((progress.totalCompleted / targetPrompts) * 100);
                    showV2Loading(
                        'Prompts', 
                        `Generating prompts: Batch ${(progress.batchIndex || 0) + 1} of ${totalBatches} (${progressPercent}% complete)...`
                    );
                }
            });
            
            if (!data) {
                throw new Error('Prompts generation failed');
            }
            
            // Extract timing data from Agent response
            const stepTiming = {
                processingTime: data.processingTime || (Date.now() - stepStartMs),
                timestamp: data.timestamp || new Date().toISOString()
            };
            
            setV2Data(prev => ({ 
                ...prev, 
                prompts: data,
                stepTimings: {
                    ...prev.stepTimings,
                    prompts: stepTiming
                }
            }));
            // Note: Prompts are not saved to session separately - they're generated from analysis
            
            // Display prompts immediately after completion
            displayPrompts(data);
            
            // Mark generation as complete
            setPromptGenerationProgress(prev => ({
                ...prev,
                isGenerating: false,
                currentBatch: 10,
                completedPrompts: data.microPrompts?.length || data.prompts?.length || 25,
                batchStatus: Array(10).fill('completed')
            }));
            
        } catch (error) {
            
            // Reset batch progress on error
            setPromptGenerationProgress(prev => ({
                ...prev,
                isGenerating: false,
                batchStatus: prev.batchStatus.map(status => 
                    status === 'processing' ? 'failed' : status
                )
            }));
            
            // Get fresh session data for error context (in case the error happened before we fetched it above)
            let errorContext = {};
            try {
                const errorSessionData = await api.getSession(sessionId.current);
                const sessionData = errorSessionData?.session || {};
                errorContext = {
                    hasAnalysisData: !!sessionData.analysis,
                    hasResearchData: !!sessionData.research,
                    hasValidationData: !!sessionData.validation,
                    answersCount: sessionData.answers?.length || 0,
                    originalIdeaLength: sessionData.idea?.length || 0
                };
            } catch (sessionError) {
                // Fallback to v2Data if session fetch fails
                errorContext = {
                    hasAnalysisData: !!v2Data.analysis,
                    hasResearchData: !!v2Data.research,
                    hasValidationData: !!v2Data.validation,
                    answersCount: v2Data.answers?.length || 0
                };
            }
            
            const classification = logAPIError('prompts', error, targetPlatform, errorContext);
            
            // Check if it's a retryable error - prefer backend-processed error properties
            const isRetryable = error._processedByErrorHandler ? error.isRetryable : classification.isRetryable;
            const serviceName = error._processedByErrorHandler ? error.service : classification.service;
            const errorType = error._processedByErrorHandler ? error.errorType : classification.type;
            
            if (isRetryable) {
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                setupStepRetry('prompts', {
                    platform: targetPlatform
                });
                showPromptsError(`${serviceName} API is having issues (${errorType}). Your progress has been saved - ${errorMsg.user}`);
                // Throw special error to stop the flow
                const retryError = new Error('API_RETRY_NEEDED');
                retryError.isRetryError = true;
                throw retryError;
            } else {
                // For other errors, use centralized error message
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                showPromptsError(errorMsg.user || 'Failed to generate prompts');
                throw new Error(errorMsg.user);
            }
        } finally {
            hideLoading();
        }
    }, [showV2Loading, hideLoading, showPromptsError, displayPrompts]);

    // Step 6: Diagram Generation (Cloudflare Agents SDK)
    const runDiagramStep = useCallback(async (platform = null) => {
        setCurrentStep(6);

        try {
            // Check if diagrams already exist before generating
            const sessionData = await api.getSession(sessionId.current);
            if (!sessionData?.success || !sessionData?.session) {
                throw new Error('Session data not available for diagram generation');
            }
            
            // Check if flow diagram already exists
            const flowDiagramData = sessionData.session.flowDiagram || sessionData.session.flow_diagram;
            if (flowDiagramData && typeof flowDiagramData === 'object' && Object.keys(flowDiagramData).length > 0) {
                
                // Update v2Data with existing diagram data
                setV2Data(prev => ({
                    ...prev,
                    flowDiagram: flowDiagramData
                }));
                
                hideLoading();
                return;
            }

            showV2Loading('Generating flow diagrams...', 'This may take 15-30 seconds');

            const analysisData = sessionData.session.analysis;
            if (!analysisData) {
                throw new Error('Analysis data not available for diagram generation');
            }

            // Get current user ID for credit tracking
            const userId = v2Data.userId || api.getCurrentUserId();
            
            const result = await api.generateFlowDiagramWithUserId(analysisData, sessionId.current, userId, false);
            
            // Handle Agent SDK polling response
            if (result.status === 'started' || result.status === 'in_progress') {
                
                // Poll for completion
                let completed = false;
                let attempts = 0;
                const maxAttempts = 12; // 60 seconds max (12 × 5s = 60s)
                
                while (!completed && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 5000)); // ✅ OPTIMIZED: 5s instead of 2s
                    attempts++;
                    
                    try {
                        const statusResult = await api.request(`/api/v2/flow-diagram/status?taskId=${result.taskId}&agentId=${result.agentId}`);
                        
                        if (statusResult.status === 'completed' && statusResult.diagram) {
                            
                            // Update v2Data with diagram results
                            setV2Data(prev => ({
                                ...prev,
                                flowDiagram: statusResult.diagram
                            }));
                            
                            // Save to session  
                            await saveToSession('flow_diagram', statusResult.diagram);
                            
                            completed = true;
                            break;
                        } else if (statusResult.status === 'failed') {
                            throw new Error(statusResult.error || 'Diagram generation failed');
                        }
                        // Continue polling if still in progress
                    } catch (pollError) {
                        if (attempts >= maxAttempts) {
                            throw new Error(`Diagram polling failed after ${maxAttempts} attempts: ${pollError.message}`);
                        }
                        // Continue polling on error (might be temporary)
                    }
                }
                
                if (!completed) {
                    throw new Error('Diagram generation timeout - took longer than expected');
                }
            } else if (result.mermaidDiagrams) {
                // Direct completion (legacy path)
                
                setV2Data(prev => ({
                    ...prev,
                    flowDiagram: result.mermaidDiagrams
                }));
                
                await saveToSession('flow_diagram', result.mermaidDiagrams);
            } else {
                throw new Error('No diagram data returned from API');
            }


        } catch (error) {
            
            // Classify error for user messaging
            const { errorType, serviceName } = classifyError(error);
            
            // Handle specific error types
            if (errorType === 'rate_limit') {
                throw new Error('API rate limit reached. Please wait a moment and try again.');
            } else if (errorType === 'insufficient_credits') {
                throw new Error('Insufficient credits for diagram generation. Please purchase more credits.');
            } else {
                // For other errors, use centralized error message
                const errorMsg = error._processedByErrorHandler ? 
                    { user: error.userMessage } : 
                    getErrorMessage(errorType, serviceName, error.message);
                throw new Error(errorMsg.user || 'Failed to generate flow diagrams');
            }
        } finally {
            hideLoading();
        }
    }, [showV2Loading, hideLoading, saveToSession, v2Data.userId]);

    // Main V2 Flow execution - MATCHES ORIGINAL app.js LOGIC EXACTLY
    const startV2Flow = useCallback(async () => {
        
        try {
            // Get the original idea and platform from session (SAME AS ORIGINAL)
            if (!sessionId.current) {
                throw new Error('No session ID available');
            }
            
            const sessionResponse = await api.getSession(sessionId.current);
            if (!sessionResponse || !sessionResponse.session) {
                throw new Error('Failed to get session');
            }
            
            const sessionData = sessionResponse.session;
            
            // Check if session is already completed
            if (sessionData.status === 'completed' && sessionData.analysis && sessionData.prompts) {
                
                
                // Restore all existing data
                setV2Data(prev => ({
                    ...prev,
                    originalIdea: sessionData.idea,
                    platform: sessionData.platform,
                    research: sessionData.research,
                    validation: sessionData.validation,
                    answers: sessionData.answers || [],
                    analysis: sessionData.analysis,
                    prompts: sessionData.prompts,
                    flowDiagram: sessionData.flowDiagram || sessionData.flow_diagram,
                    userId: prev.userId
                }));
                
                // Show platform indicator
                showPlatformIndicator(sessionData.platform);
                
                // Set current step based on what data exists
                let restoredStep = 1;
                if (sessionData.research) restoredStep = 2;
                if (sessionData.validation) restoredStep = 3;
                if (sessionData.analysis) restoredStep = 4;
                if (sessionData.prompts) restoredStep = 5;
                if (sessionData.flowDiagram || sessionData.flow_diagram) restoredStep = 6;
                
                setCurrentStep(restoredStep);
                
                // Display the results immediately
                if (sessionData.analysis) {
                    setShowAnalysisResults(true);
                    setShowPromptsTab(sessionData.prompts ? true : false);
                    setActiveTab('overview'); // Start with overview showing all results
                }
                
                return; // Exit early, don't run the workflow again
            }
            
            // For new/incomplete sessions, continue with normal flow and restore answers
            setV2Data(prev => ({
                ...prev,
                originalIdea: sessionData.idea,
                platform: sessionData.platform,
                answers: sessionData.answers || []
            }));
            
            
            // Show platform indicator (SAME AS ORIGINAL)
            showPlatformIndicator(sessionData.platform);
            
            
            // Skip Step 1: Questions (already answered in Home.jsx and stored in session)
            setCurrentStep(2); // Start from research step
            
            // Step 2: Research (Claude) - Gets architecture with user answers + platform
            // Pass answers directly from sessionData instead of relying on React state
            await runResearchStep(sessionData.platform, sessionData.idea, sessionData.answers);
            
            // Get the research data that was just saved to pass to next steps
            const currentSessionData = await api.getSession(sessionId.current);
            const researchData = currentSessionData.session.research;
            
            // Step 3: Validation (OpenAI) - Validate architecture + answers + platform
            await runValidationStep(sessionData.platform, sessionData.idea, researchData, sessionData.answers);
            
            // Get the validation data that was just saved to pass to next steps
            const updatedSessionData = await api.getSession(sessionId.current);
            const validationData = updatedSessionData.session.validation;
            
            // Step 4: Analysis (Claude + OpenAI) - Final recommendations with platform
            await runAnalysisStep(sessionData.platform, sessionData.idea, researchData, validationData, sessionData.answers);
            
            // Step 5: Prompts Generation - Create development prompts
            await runPromptsStep(sessionData.platform);
            
            // Step 6: Diagram Generation - Create flow diagrams
            await runDiagramStep(sessionData.platform);
            
            // Show final results including prompts and diagrams
            // Access current values directly instead of from closure
            setV2Data(currentV2Data => {
                displayAnalysisResults(currentV2Data.analysis, currentV2Data.prompts);
                return currentV2Data; // No state change
            });
            
        } catch (error) {
            
            // Don't show generic error if it's a retry error (retry UI is already shown)
            if (error.isRetryError) {
                return; // Stop flow, retry UI is already set up
            }
            
            // Don't show generic error for insufficient credits - let React components handle it
            if (error.type === 'insufficient_credits' || error.status === 402) {
                return; // Error is already set by the step handler
            }
            
            showError('Failed to complete analysis. Please try again.');
        }
    }, [runQuestionsStep, runResearchStep, runValidationStep, runAnalysisStep, runPromptsStep, runDiagramStep, showPlatformIndicator, displayAnalysisResults, showError]);

    // Update step functions ref when functions are ready
    useEffect(() => {
        stepFunctionsRef.current = {
            runQuestionsStep,
            runResearchStep,
            runValidationStep,
            runAnalysisStep,
            runPromptsStep,
            runDiagramStep
        };
    }, [runQuestionsStep, runResearchStep, runValidationStep, runAnalysisStep, runPromptsStep, runDiagramStep]);

    // ===== 🚀 WORKFLOW METHODS =====

    // Start async workflow
    const startWorkflowAsync = useCallback(async (idea, platform, answers = []) => {
        try {
            // Use passed parameters or fallback to v2Data
            const workflowIdea = idea || v2Data.originalIdea;
            const workflowPlatform = platform || v2Data.platform;
            const workflowAnswers = answers.length > 0 ? answers : (v2Data.answers || []);
            
            // Validate required data
            if (!workflowIdea || !workflowPlatform) {
                throw new Error('Missing required data: idea and platform');
            }

            setIsWorkflowMode(true);
            setIsLoading(true);
            setCurrentStep(1);
            clearError();

            // Start workflow
            const response = await api.request('/api/v2/workflow/start', 'POST', {
                idea: workflowIdea,
                platform: workflowPlatform,
                answers: workflowAnswers,
                options: {
                    enableWebSearch: true,
                    sessionId: sessionId.current
                    // userId no longer needed - backend gets it from requireAuth
                }
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to start workflow');
            }

            // 🔄 UNIFIED ARCHITECTURE: Agent is ALWAYS created (queued or not)
            // Connect to agent WebSocket - agent state sync handles queue updates
            const { workflowId, agentId, statusUrl, status, position, estimatedWaitMinutes } = response;
            const isQueued = status === 'queued';

            const newWorkflowState = {
                workflowId,
                agentId,
                statusUrl,
                status: status || 'started'
            };

            setWorkflowState(newWorkflowState);

            // Start workflow in context (connects to agent WebSocket)
            // Agent's setState() auto-syncs queue position and workflow progress
            startContextWorkflow({
                workflowId,
                agentId,
                sessionId: sessionId.current,
                status,
                position,
                estimatedWaitMinutes
            });

            if (isQueued) {
                setIsLoading(false);
                return { queued: true, position };
            }

            // Note: Workflow state now persisted in backend only - no localStorage needed
            // WebSocket handles real-time updates via WorkflowContext

            return { workflowId, agentId };

        } catch (error) {
            setIsWorkflowMode(false);
            showError(error);
            throw error;
        }
    }, [v2Data, showError, clearError]);

    // Check if workflow can resume
    const canResumeWorkflow = useCallback(async () => {
        if (!workflowState?.workflowId || !workflowState?.agentId) {
            return false;
        }

        try {
            const status = await api.request(`/api/v2/workflow/status?workflowId=${workflowState.workflowId}&agentId=${workflowState.agentId}`);
            return status.success && !['completed', 'failed', 'cancelled'].includes(status.status);
        } catch {
            return false;
        }
    }, [workflowState]);

    // Resume workflow via WebSocket
    const resumeWorkflow = useCallback(() => {
        if (workflowState?.workflowId && workflowState?.agentId) {
            // Reconnect WebSocket via context
            startContextWorkflow({
                workflowId: workflowState.workflowId,
                agentId: workflowState.agentId,
                sessionId: sessionId.current
            });
        }
    }, [workflowState, startContextWorkflow]);

    // Resume detected workflow
    const resumeDetectedWorkflow = useCallback(() => {
        if (!resumeWorkflowData) return;

        const { workflowState: resumeState, status, workflowId, agentId } = resumeWorkflowData;

        // Restore workflow state
        setWorkflowState(resumeState);
        setIsWorkflowMode(true);
        setIsLoading(true);

        // Resume data from where we left off
        if (status.data) {
            updateStepData(status.data);
        }

        if (status.currentStep) {
            setCurrentStep(getStepNumber(status.currentStep));
        }

        // Connect WebSocket via context
        startContextWorkflow({ workflowId, agentId, sessionId: sessionId.current });

        // Clear resume data
        setResumeWorkflowData(null);
    }, [resumeWorkflowData, updateStepData, getStepNumber, startContextWorkflow]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setWorkflowState(null);
            setIsWorkflowMode(false);
        };
    }, []);

    // 🔄 UNIFIED: Cancel from queue - use workflow cancel (agent handles it)
    const cancelQueue = useCallback(() => {
        cancelContextWorkflow();
        setIsLoading(false);
        setIsWorkflowMode(false);
    }, [cancelContextWorkflow]);

    return {
        // State
        v2Data,
        currentStep,
        isLoading,
        loadingTitle,
        loadingMessage,
        error,
        activeTab,
        // Production mode - debug state removed
        showPromptModal,
        currentPromptModalData,
        toasts,
        statusTags,
        flowDiagramLoaded,
        flowDiagramError,
        analysisError,
        promptsError,
        showAnalysisResults,
        showPromptsTab,
        shouldStartQuestions,
        stepRetryInfo: null,
        showRetryOption: false,
        sessionId: sessionId.current,
        
        // Batch generation progress
        promptGenerationProgress,

        // Session reuse state
        activeSession,
        sessionCheckLoading,
        showResumeDialog,

        // Actions
        initializeSession,
        setSessionId,
        loadSession,
        resumeSession,
        startV2Flow,
        runResearchStep,
        runValidationStep,
        runAnalysisStep,
        runPromptsStep,
        runDiagramStep,
        // Production mode - debug actions removed
        openPromptModal,
        closePromptModal,
        copyPromptToClipboard,
        switchToTab,
        loadFlowDiagram,
        showPlatformIndicator,
        clearError,
        showError,
        showAnalysisError,
        showPromptsError,
        retryAnalysis,
        retryPrompts,
        retryFromStep: () => {},
        setupStepRetry,
        saveToSession,
        showToast,
        removeToast,
        displayAnalysisResults,
        displayPrompts,
        showPromptModalWithContent,
        handleNeedMoreInfo,
        loadPromptResults,

        // Session reuse actions
        checkForActiveSession,
        createNewSession,
        handleStartFresh,

        // Workflow actions
        startWorkflowAsync,
        cancelWorkflow,
        canResumeWorkflow,
        resumeWorkflow,
        resumeDetectedWorkflow,
        workflowState,
        isWorkflowMode,
        resumeWorkflowData,

        // 🔄 UNIFIED QUEUE: Queue status is now part of workflowState
        // When workflowState.status === 'queued', the queue fields are populated
        // 🔄 UNIFIED: Queue status derived from workflowState (no separate queueState)
        queueStatus: workflowState?.status === 'queued' ? {
            position: workflowState.queuePosition,
            estimatedWait: workflowState.queueEstimatedWait,
            message: workflowState.queueMessage,
        } : null,
        isQueueMode: workflowState?.status === 'queued',
        cancelQueue,

        // Setters for direct state updates (if needed)
        setV2Data,
        setCurrentStep,
        setActiveTab,
        setStatusTags,
        // Production mode - debug setters removed
    };
} 